import { api } from '../../core/network/api';
import type { ItemCopy } from '../config/item-copy';

/** design/gdd/rites.md REST surface — plain wrappers, same convention as consumables-api.ts. */

/** Mirrors the backend's `RitePaysOn`. */
export type RitePaysOn = 'EXTRACTED' | 'DIED';

/**
 * Mirrors the backend's `RiteDef`.
 *
 * Every effect field is optional and every one of them is a *number read off
 * this row*, never a phrase written on the client. `ui/rite-copy.ts` turns
 * them into a sentence — the same split `talent-copy.ts` settled on, and the
 * reason the HUD stopped shipping numbers that disagreed with the server.
 */
export interface RiteDefRow {
  id: string;
  name: string;
  priceWax: number;
  paysOn: RitePaysOn;
  requiresStratum?: 'CELLARS' | 'OSSUARY' | 'VAULTS';
  xpMultiplier?: number;
  maxBonusXp?: number;
  killXpMultiplier?: number;
  completionXpMultiplier?: number;
  partyXpMultiplier?: number;
  carryBonusKg?: number;
  rareChanceBonus?: number;
  corpseLootCertain?: boolean;
  corpseRareMultiplier?: number;
  silentLoad?: boolean;
  /** Optional for the reason `ConsumableDefRow.copy` is: a client can outlive the server build it talks to. */
  copy?: ItemCopy;
}

export interface RitesResponse {
  catalog: RiteDefRow[];
  /** defId -> charges owned, account-level. */
  stock: Record<string, number>;
  /** The Rite the next run will burn, or null. */
  selectedRiteDefId: string | null;
}

export const getRites = (): Promise<RitesResponse> => api.get('/rites');

export const purchaseRite = (
  defId: string, quantity: number,
): Promise<{ stock: Record<string, number>; softBalance: number; selectedRiteDefId: string | null }> =>
  api.post('/rites/purchase', { defId, quantity });

/** `null` chooses to burn nothing, which is a real choice and not an absence. */
export const selectRite = (defId: string | null): Promise<{ selectedRiteDefId: string | null }> =>
  api.post('/rites/select', { defId });
